
import React from "react";
import { Container } from "@/components/ui/container";
import { FileText, Users, Shield, Gavel, Heart, Zap, Target, MessageCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const Terms = () => {
  const sections = [
    {
      id: "intro",
      icon: FileText,
      title: "Introduction",
      description: "Overview of our terms and your agreement with TeenVisor."
    },
    {
      id: "accounts",
      icon: Users,
      title: "User Accounts",
      description: "Requirements and responsibilities for account creation and maintenance."
    },
    {
      id: "content",
      icon: Shield,
      title: "Content Policy",
      description: "Guidelines for content creation and community interactions."
    },
    {
      id: "conduct",
      icon: Gavel,
      title: "Code of Conduct",
      description: "Expected behavior and prohibited activities on our platform."
    },
    {
      id: "intellectualProperty",
      icon: Heart,
      title: "Intellectual Property",
      description: "Rights and ownership of content and platform materials."
    },
    {
      id: "disclaimer",
      icon: Zap,
      title: "Disclaimers",
      description: "Limitations of liability and service warranties."
    },
    {
      id: "termination",
      icon: Target,
      title: "Account Termination",
      description: "Circumstances and procedures for account suspension or termination."
    },
    {
      id: "contact",
      icon: MessageCircle,
      title: "Contact Information",
      description: "How to reach us regarding these terms and conditions."
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">      
      {/* Hero Section */}
      <div className="relative py-20 px-4 text-center overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse-gentle"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse-gentle"></div>
        </div>
        
        <Container className="relative">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-6 animate-float">
            <FileText className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-primary/80 to-secondary bg-clip-text text-transparent leading-tight">
            Terms of Service
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
            Please review these terms carefully. By using TeenVisor, you agree to be bound by these terms and conditions.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            Effective Date: May 31, 2025
          </div>
        </Container>
      </div>

      {/* Quick Navigation */}
      <Container className="py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-12">
          {sections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className="group flex flex-col items-center p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 hover:bg-card/80 transition-all duration-300 hover:scale-105"
            >
              <section.icon className="h-6 w-6 text-primary mb-2 group-hover:scale-110 transition-transform" />
              <span className="text-xs font-medium text-center group-hover:text-primary transition-colors leading-tight">
                {section.title}
              </span>
            </a>
          ))}
        </div>
      </Container>

      <main className="flex-grow pb-20">
        <Container>
          <div className="max-w-4xl mx-auto space-y-16">
            
            {/* Introduction Section */}
            <Card id="intro" className="border-0 shadow-lg bg-gradient-to-r from-primary/5 to-secondary/5">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                    <FileText className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">Terms of Service Agreement</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      These Terms of Service ("Terms") constitute a legally binding agreement between you and TeenVisor Inc. ("Company," "we," "us," or "our") governing your access to and use of the TeenVisor platform, including all content, functionality, and services offered on or through our website and mobile applications.
                    </p>
                  </div>
                </div>
                
                <div className="bg-background/50 p-6 rounded-lg border border-border/50">
                  <p className="font-semibold mb-4 text-lg">Agreement to Terms:</p>
                  <ul className="space-y-3 text-muted-foreground leading-relaxed">
                    <li>• By accessing or using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms</li>
                    <li>• If you do not agree with any provision of these Terms, you must not use our services</li>
                    <li>• These Terms apply to all users of the service, including without limitation users who are browsers, vendors, customers, merchants, and/or contributors of content</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* User Accounts Section */}
            <Card id="accounts" className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-500/10 rounded-lg">
                    <Users className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">User Account Requirements</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      To access certain features of our platform, you must create and maintain a user account in accordance with the following requirements and responsibilities.
                    </p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Users className="h-5 w-5 text-primary" />
                      Account Registration
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• You must be at least 13 years of age to create an account</li>
                      <li>• All registration information must be accurate, current, and complete</li>
                      <li>• You must provide a valid email address for account verification</li>
                      <li>• One account per person is permitted</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Shield className="h-5 w-5 text-primary" />
                      Account Security
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• You are responsible for maintaining the confidentiality of your login credentials</li>
                      <li>• You must notify us immediately of any unauthorized use of your account</li>
                      <li>• We reserve the right to suspend accounts showing suspicious activity</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Content Policy Section */}
            <Card id="content" className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-green-500/10 rounded-lg">
                    <Shield className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">Content Guidelines and Policies</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      Users are responsible for all content posted on TeenVisor. Content must comply with our community guidelines and applicable laws.
                    </p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Shield className="h-5 w-5 text-primary" />
                      Prohibited Content
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• Content that is illegal, harmful, threatening, abusive, or defamatory</li>
                      <li>• Hate speech, discrimination, or harassment of any kind</li>
                      <li>• Spam, unauthorized advertising, or commercial solicitation</li>
                      <li>• Content that infringes on intellectual property rights</li>
                      <li>• False, misleading, or deceptive information</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Heart className="h-5 w-5 text-primary" />
                      Content Ownership
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• You retain ownership of content you create and post</li>
                      <li>• By posting content, you grant TeenVisor a license to use, modify, and distribute it</li>
                      <li>• We may remove content that violates these terms without notice</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Code of Conduct Section */}
            <Card id="conduct" className="border-0 shadow-lg bg-gradient-to-r from-green-500/5 to-blue-500/5">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-green-500/10 rounded-lg">
                    <Gavel className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">Code of Conduct</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      All users must conduct themselves in a respectful and professional manner while using TeenVisor services.
                    </p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Gavel className="h-5 w-5 text-primary" />
                      Expected Behavior
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• Treat all community members with respect and dignity</li>
                      <li>• Communicate constructively and professionally</li>
                      <li>• Respect diverse perspectives and experiences</li>
                      <li>• Report inappropriate behavior to our moderation team</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Target className="h-5 w-5 text-primary" />
                      Prohibited Activities
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• Harassment, bullying, or intimidation of other users</li>
                      <li>• Impersonation of other individuals or organizations</li>
                      <li>• Attempting to gain unauthorized access to accounts or systems</li>
                      <li>• Circumventing or interfering with security features</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Intellectual Property Section */}
            <Card id="intellectualProperty" className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-purple-500/10 rounded-lg">
                    <Heart className="h-6 w-6 text-purple-500" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">Intellectual Property Rights</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      TeenVisor respects intellectual property rights and expects users to do the same. This section outlines ownership and usage rights for platform content.
                    </p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Heart className="h-5 w-5 text-primary" />
                      Platform Ownership
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• TeenVisor owns all rights, title, and interest in the platform and its original content</li>
                      <li>• Our trademarks, logos, and brand elements are protected intellectual property</li>
                      <li>• Platform software and algorithms are proprietary and protected by copyright</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Users className="h-5 w-5 text-primary" />
                      User Content Rights
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• Users retain ownership of original content they create and share</li>
                      <li>• By posting content, users grant TeenVisor a worldwide, royalty-free license to use, reproduce, and distribute such content</li>
                      <li>• Users warrant that their content does not infringe third-party intellectual property rights</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Disclaimers Section */}
            <Card id="disclaimer" className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-orange-500/10 rounded-lg">
                    <Zap className="h-6 w-6 text-orange-500" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">Disclaimers and Limitations</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      TeenVisor provides services on an "as-is" basis. Please review these important disclaimers regarding our liability and service warranties.
                    </p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Zap className="h-5 w-5 text-primary" />
                      Service Warranty Disclaimer
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• Services are provided "AS IS" and "AS AVAILABLE" without warranties of any kind</li>
                      <li>• We do not guarantee uninterrupted, secure, or error-free service</li>
                      <li>• We disclaim all warranties, express or implied, including merchantability and fitness for a particular purpose</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Shield className="h-5 w-5 text-primary" />
                      Limitation of Liability
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• TeenVisor shall not be liable for any indirect, incidental, special, or consequential damages</li>
                      <li>• Our total liability for any claims shall not exceed the amount paid by you for the services in the preceding 12 months</li>
                      <li>• Some jurisdictions do not allow limitation of liability, so these limitations may not apply to you</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Termination Section */}
            <Card id="termination" className="border-0 shadow-lg bg-gradient-to-r from-purple-500/5 to-pink-500/5">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-8">
                  <div className="flex items-center justify-center w-12 h-12 bg-purple-500/10 rounded-lg">
                    <Target className="h-6 w-6 text-purple-500" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-4 leading-tight">Account Termination</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      This section outlines the circumstances under which TeenVisor may terminate or suspend user accounts and the procedures involved.
                    </p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Target className="h-5 w-5 text-primary" />
                      Termination by TeenVisor
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• We may terminate accounts for violation of these Terms of Service</li>
                      <li>• Accounts engaged in illegal activities will be immediately suspended</li>
                      <li>• Repeated violations of community guidelines may result in permanent account termination</li>
                      <li>• We reserve the right to refuse service to anyone for any reason at any time</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                      <Users className="h-5 w-5 text-primary" />
                      User-Initiated Termination
                    </h3>
                    <ul className="space-y-3 text-muted-foreground leading-relaxed ml-8">
                      <li>• Users may terminate their accounts at any time through account settings</li>
                      <li>• Account deletion requests are processed within 30 days</li>
                      <li>• Some information may be retained for legal and business purposes as permitted by law</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Section */}
            <Card id="contact" className="border-0 shadow-lg bg-gradient-to-r from-primary/10 to-secondary/10">
              <CardContent className="p-8 text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6 mx-auto">
                  <MessageCircle className="h-8 w-8 text-primary" />
                </div>
                <h2 className="text-3xl font-bold mb-6 leading-tight">Contact Information</h2>
                <p className="text-muted-foreground mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
                  If you have questions about these Terms of Service or need to report a violation, please contact us using the information below.
                </p>
                
                <div className="bg-background/50 p-6 rounded-lg border border-border/50 mb-8 max-w-2xl mx-auto">
                  <h3 className="font-semibold mb-4 text-lg">Legal Department</h3>
                  <div className="space-y-2 text-muted-foreground">
                    <p>TeenVisor Inc.</p>
                    <p>Email: teenvisor@gmail.com</p>
                    <p>For urgent matters: +91 9994342931</p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <a 
                    href="mailto:teenvisor@gmail.com?subject=Terms of Service Inquiry" 
                    className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Contact Legal Team
                  </a>
                  <a 
                    href="tel:+919994342931" 
                    className="flex items-center gap-2 px-6 py-3 border border-border rounded-lg hover:bg-accent transition-colors"
                  >
                    <FileText className="h-4 w-4" />
                    Emergency Contact
                  </a>
                </div>
              </CardContent>
            </Card>

          </div>
        </Container>
      </main>
    </div>
  );
};

export default Terms;
